<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-10">
      <h4 class="mb-5">Ready for your Forensic Scientist experience? Let’s dive in!</h4>

      <p class="mb-5">
        Forensic scientists are a bit like detectives (but you get to wear overalls). Primarily,
        your job will be to intricately and expertly examine crime scenes to find any evidence
        relevant to an alleged crime.
      </p>
      <p class="mb-5">
        From a small crime to a huge investigation, forensics help law enforcement, courts and
        lawyers piece things together. If you’ve seen any crime shows, you’ll know that any evidence
        is crucial in helping find the truth and assist in any legal investigations.
      </p>

      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-3/4 lg:w-2/3 w-full my-5">
          <p class="mb-5 text-bold">Specialise In Various Areas</p>
          <p class="mb-5">
            Although a wide range of expertise is required for forensic investigations, a forensic
            scientist can choose to specialise in:
          </p>
          <ul class="default-list">
            <li>Chemistry</li>
            <li>Biochemistry</li>
            <li>Molecular biology</li>
            <li>Botany</li>
            <li>Pharmacology</li>
            <li>Toxicology</li>
            <li>Crime scene examination</li>
            <li>Firearms examination</li>
            <li>Fingerprint/ document examination</li>
          </ul>

          <p class="mb-5 text-bold">Employment Opportunities</p>
          <p class="mb-5">
            A career in crime scene investigation, fingerprint identification, or firearms
            examination usually requires entry into the police force. It also might include
            completion of national training in your chosen discipline.
          </p>
          <p class="mb-5">The main employers of forensic scientists are:</p>
          <ul class="default-list">
            <li>Health departments (state and federal)</li>
            <li>Police forces (government, state, territory and federal)</li>
          </ul>

          <p class="mb-5">
            In Canberra, the Australian Federal Police employs forensic scientists specialising in:
          </p>
          <ul class="default-list">
            <li>Crime scene examination</li>
            <li>Fingerprint identification</li>
            <li>Firearms and ammunition identification</li>
            <li>Document examination</li>
            <li>Forensic biology</li>
            <li>Forensic chemistry</li>
          </ul>

          <p class="mb-5 text-underline text-bold">Education:</p>
          <p class="mb-5">
            To become a forensic scientist, you usually have to study forensic science at university
            (preferably at honours level), followed by a postgraduate qualification in forensic
            science.
          </p>
          <p class="mb-5">
            You could also study other degrees in relevant areas, which might provide entry into
            various areas of forensic science.
          </p>
          <p class="mb-5">
            Relevant areas include biology, botany, chemistry, physics, dentistry or medicine.
          </p>
          <p class="mb-5">
            As you can see, there’s more than one pathway to becoming a crime scene expert!
          </p>
        </div>
        <div class="vx-col xl:w-1/4 lg:w-1/3 w-full my-5">
          <img
            src="@/assets/images/experiences/forensic_science/intro_sidebar.jpg"
            alt=""
            class="mx-auto img-responsive"
          />
        </div>
      </div>
    </vx-card>

    <vx-card title="Quick Details">
      <div class="vx-row flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full mb-5">
          <h4 class="mb-5 text-underline">Requirements:</h4>
          <ul class="default-list">
            <li>Good communication skills</li>
            <li>Able to remain unbiased</li>
            <li>Meticulous</li>
            <li>Capable of logical and lateral thinking</li>
            <li>Able to work as part of a team</li>
            <li>Initiative and motivation</li>
            <li>Perseverance</li>
            <li>Good observation skills</li>
            <li>Balanced and measured nature</li>
          </ul>

          <h4 class="my-5 text-underline">Tasks:</h4>
          <ul class="default-list">
            <li>Identify illicit drugs</li>
            <li>
              Analyse drugs and poisons in human tissue and body fluids, including blood alcohol
              results
            </li>
            <li>Examine and compare materials such as fibres or gunshot residues</li>
            <li>Use DNA profiling</li>
            <li>Conduct botanical identification of plant materials (cannabis, for example)</li>
            <li>
              Conduct document examinations, both physical (handwriting and typewriting) and
              chemical (inks and papers)
            </li>
            <li>Obtain data from electronic devices</li>
            <li>Examine crime scenes</li>
            <li>Identify firearms and ammunition</li>
            <li>
              Detect, enhance, recover and identify fingerprints, footprints, tyre marks or tracks
            </li>
            <li>Examine fire and explosion scenes to establish the origin and cause</li>
            <li>Improve the clarity of, and analyse, audio and video recordings</li>
            <li>Appear in court and present scientific or opinion testimony</li>
            <li>Present to a variety of audiences on the work of forensic scientists</li>
            <li>
              Provide advice to police investigators, legal practitioners, scientists and
              pathologists
            </li>
            <li>Study the forms and structures of parasites and toxins</li>
            <li>Design and conduct experiments</li>
            <li>Analyse data</li>
            <li>Present findings at scientific meetings and conferences</li>
          </ul>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full mb-5">
          <img
            src="@/assets/images/experiences/forensic_science/gug_stats_panel.jpg"
            alt="Job statistics panel: average age 37 years, moderate future growth, 63% female, 42 hours average full-time week, $1,794 weekly pay, very high skill level rating, average unemployment, 70% full-time, 1,000 workers. Age brackets: 15 to 19, 0.3%; 20 to 24, 5%; 25 to 34, 35%; 35 to 44, 30.8%; 45 to 54, 16.9%; 55 to 59, 5.5%; 60 to 64, 3.9%; 65 and over, 2.6%. Education level: advanced diploma or diploma 3.4%; bachelor degree 40.9%; certificate III or IV 4.1%; postgraduate or graduate diploma or graduate certificate 48.5%; year 10 and below 0%; year 11, 0%; year 12, 3%."
            class="mx-auto img-responsive"
          />
        </div>
      </div>
      <vs-alert color="dark" title="Caution" active="true">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
    </vx-card>

    <vx-card>
      <p class="mb-5">
        Want to find out how Forensic Science is advancing as well as the future of Forensic Science
        in Australia?
      </p>
      <p class="mb-5">Check out the video below.</p>
      <responsive-youtube-wrapper :image="videos[1].image" :video="videos[1].url" />
    </vx-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      videoTrigger1: require('@/assets/images/experiences/psychology/becoming_a_registered_psychologist_trigger.jpg'),
      videos: {
        1: {
          image: require('@/assets/images/experiences/forensic_science/forensics_australia_splash.jpg'),
          url: 'https://www.youtube.com/embed/VRJqkCqj70I',
        },
      },
      activeTab: 0,
    };
  },
};
</script>
